import { KitButton } from '@omni/kit/components';
import { IconName } from '@omni/kit/components/KitIcon';
import {
  KitSnackDuration,
  KitSnackRender,
} from '@omni/kit/components/KitSnack';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { colorForScheme } from '@omni/kit/theming/Theming';
import { useNavigation } from '@react-navigation/native';
import { User } from '@sendbird/chat';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Platform, ScrollView, StyleSheet, View } from 'react-native';
import Modal from 'react-native-modal';
import { useDispatch, useSelector } from 'react-redux';

import { useChannelGroup } from '../../../hooks/useChannelGroup';
import { ChannelMember } from '../../../mobile/Types';
import ChannelDetailHeader from '../../../mobile/components/channelDetails/ChannelDetailHeader';
import MemberActionSheet from '../../../mobile/components/channelDetails/MemberActionSheet';
import ActionSheetItem from '../../../mobile/components/chat/actionSheet/ActionSheetItem';
import ConfirmModal from '../../../mobile/components/web/ConfirmModal';
import { sbGetChannelMetadata } from '../../../utilities/sendbird/channelFunctions';
import * as Constants from '../../Constants';
import {
  blockUser,
  changeManagerToUser,
  changeUserToManager,
  getBlockedUserList,
  getInvitedList,
  getMemberList,
  removeUser,
  unblockUser,
} from '../../redux/actions/ChatActions';
import {
  clearSelectedGroupChannel,
  deleteChannel,
  hideChannel,
  onLeaveChannelPress,
} from '../../redux/actions/SystemActions';
import {
  appKeySelector,
  channelSelector,
  memberListSelector,
  ownerListSelector,
} from '../../redux/selectors';
import { ChannelType } from '../../redux/types';
import { DetailRow, MemberSection } from './components/details';

const debug = require('debug')('tca:chat:screen:ChannelDetailsScreen');

export default function ChannelDetailsScreen(): JSX.Element {
  const [isSelectedUserOwner, setIsSelectedUserOwner] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ChannelMember | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [managersExpanded, setManagersExpanded] = useState<boolean>(false);
  const [membersExpanded, setMembersExpanded] = useState<boolean>(false);
  const [isRestricted, setIsRestricted] = useState<boolean>(false);
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
  });

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmActionButtonText, setConfirmActionButtonText] = useState('');
  const [confirmActionTitle, setConfirmActionTitle] = useState('');
  const [confirmActionMessage, setConfirmActionMessage] = useState('');
  const [confirmActionKey, setConfirmActionKey] = useState('');

  const ownerList = useSelector(ownerListSelector);
  const memberList = useSelector(memberListSelector);

  // dev note: this channel becomes undefined when you 'leave' a channel
  const channel = useSelector(channelSelector) as GroupChannel;

  const appKey = useSelector(appKeySelector);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  // Group Data
  const group = useChannelGroup(channel?.url);
  const isManager = channel?.myRole === 'operator';
  const allowInvites = isManager || group?.enrollment_type === 'open';

  //****************************************************************************
  // Methods
  //****************************************************************************

  const updateMemberList = () => {
    dispatch(getMemberList(channel?.url));
  };

  const updateBlockedUserList = () => {
    dispatch(getBlockedUserList());
  };

  const updateInvitedList = () => {
    dispatch(getInvitedList(channel?.url));
  };

  const updateIsRestricted = () => {
    const keys = ['restricted'];
    sbGetChannelMetadata(channel, keys).then((metadata: any) => {
      setIsRestricted(metadata.restricted === 'true');
    });
  };

  const parseChannelData = useCallback(() => {
    try {
      const data = JSON.parse(channel?.data);
      debug('channel data ', data);

      return data;
    } catch {
      debug('Could not parse channel data');
    }
  }, [channel]);

  const _onMemberEditPress = (member: ChannelMember) => {
    setSelectedUser(member);
    setIsSelectedUserOwner(false);
    _toggleModal();
  };

  const _onOwnerEditPress = (member: ChannelMember) => {
    setSelectedUser(member);
    setIsSelectedUserOwner(true);
    _toggleModal();
  };

  const _askLeaveChannel = () => {
    const title = `Leave ${channel?.name}`;
    const msg =
      'Are you sure you want to leave this group? You will no longer have access to the group or its messages.';
    _showConfirmAlert(title, msg, 'Leave', 'leave-channel');
  };

  const _askDeleteChannel = () => {
    const title = 'Delete group';
    const msg = `Are you sure you want to delete this group? Deleting "${channel?.name}" cannot be undone.`;
    _showConfirmAlert(title, msg, 'Delete', 'delete-channel');
  };

  const _askHideConversation = () => {
    const title = 'Confirm Hide Conversation';
    const msg =
      'Hiding this conversation removes it from your conversation list, but will not delete it for other users.';
    _showConfirmAlert(title, msg, 'Hide', 'hide-conversation');
  };

  const _confirmLeaveChannel = () => {
    dispatch(onLeaveChannelPress(channel));

    // goBack is needed for web and for native messaging on large screens
    navigation.goBack();

    if (Platform.OS !== 'web') {
      navigation.navigate(Constants.CHANNEL_LIST_SCREEN);
    }

    showKitSnack(`You left ${channel?.name}`, KitSnackDuration.LONG);
  };

  const _confirmDeleteChannel = () => {
    debug('Confirm Delete Channel');
    dispatch(deleteChannel(channel?.url));

    // goBack is needed for web and for native messaging on large screens
    navigation.goBack();

    if (Platform.OS !== 'web') {
      navigation.navigate(Constants.CHANNEL_LIST_SCREEN);
    }
  };

  const _confirmHideConversation = () => {
    debug('Confirm Hide Conversation');
    dispatch(hideChannel(channel));

    // goBack is needed for web and for native messaging on large screens
    navigation.goBack();

    if (Platform.OS !== 'web') {
      navigation.navigate(Constants.CHANNEL_LIST_SCREEN);
    }
  };

  const _confirmUnblockUser = (chan: GroupChannel, user: User) => {
    dispatch(unblockUser(chan, user));
    setSelectedUser(null);
  };

  const _confirmBlockUser = (chan: GroupChannel, user: User) => {
    dispatch(blockUser(chan, user));
    setSelectedUser(null);
  };

  const _confirmPromoteUser = (user: ChannelMember) => {
    dispatch(changeUserToManager(channel as GroupChannel, user));
    showKitSnack(
      `${selectedUser?.nickname} changed to Manager`,
      KitSnackDuration.SHORT
    );
    setSelectedUser(null);
  };

  const _confirmDemoteUser = (user: ChannelMember) => {
    dispatch(changeManagerToUser(channel as GroupChannel, user, appKey));
    showKitSnack(
      `${selectedUser?.nickname} changed to Member`,
      KitSnackDuration.SHORT
    );
    setSelectedUser(null);
  };

  const _confirmRemoveUser = (chan: GroupChannel, member: ChannelMember) => {
    dispatch(removeUser(chan, member));
    setSelectedUser(null);
  };

  const _navToMemberListScreen = (managers = false) => {
    navigation.navigate(Constants.MEMBER_LIST_SCREEN, {
      title: managers ? 'Managers' : 'Members',
      showManagers: managers,
      allowInvites,
    });
  };

  const _renderChannelDetails = () => {
    return (
      <View style={styles.viewContainer}>
        <ChannelDetailHeader
          coverUrl={channel?.coverUrl}
          name={channel?.name}
          description={channelData?.description}
        />
        <View
          style={{
            marginVertical: Spacing.xl,
            borderTopWidth: 1,
            borderTopColor: Colors.N100,
          }}
        >
          <DetailRow
            expanded={managersExpanded}
            leftLabel='Managers'
            count={ownerList.length}
            onPress={() => {
              if (Platform.OS === 'web') {
                setManagersExpanded(!managersExpanded);
              } else {
                _navToMemberListScreen(true);
              }
            }}
          >
            {managersExpanded ? (
              <MemberSection
                showManagersOnly
                isOwner={isManager}
                isRestricted={isRestricted}
                onMemberEditPress={_onMemberEditPress}
                onOwnerEditPress={_onOwnerEditPress}
                renderMemberWebActionSheet={_renderWebActionSheet(false)}
                renderOwnerWebActionSheet={_renderWebActionSheet(true)}
              />
            ) : null}
          </DetailRow>
          <DetailRow
            expanded={membersExpanded}
            leftLabel='Members'
            count={memberList.length - ownerList.length}
            onPress={() => {
              if (Platform.OS === 'web') {
                setMembersExpanded(!membersExpanded);
              } else {
                _navToMemberListScreen(false);
              }
            }}
          >
            {membersExpanded ? (
              <MemberSection
                showMembersOnly
                isOwner={isManager}
                isRestricted={!allowInvites}
                onMemberEditPress={_onMemberEditPress}
                onOwnerEditPress={_onOwnerEditPress}
                renderMemberWebActionSheet={_renderWebActionSheet(false)}
                renderOwnerWebActionSheet={_renderWebActionSheet(true)}
              />
            ) : null}
          </DetailRow>
        </View>
        {(channel?.myRole !== 'operator' || ownerList.length > 1) && (
          <View style={{ marginTop: Spacing.l }}>
            <KitButton
              secondary
              title='Leave group'
              onPress={_askLeaveChannel}
            />
          </View>
        )}
        {isManager && (
          <View style={{ marginTop: Spacing.l }}>
            <KitButton
              secondary
              title='Delete group'
              onPress={_askDeleteChannel}
            />
          </View>
        )}
        <View style={{ height: Spacing.xxl }} />
      </View>
    );
  };

  const _renderDirectDetails = () => {
    return (
      <View style={styles.viewContainer}>
        <MemberSection
          isRestricted={true}
          isDirect={true}
          isOwner={false}
          onMemberEditPress={_onMemberEditPress}
          onOwnerEditPress={_onOwnerEditPress}
          renderMemberWebActionSheet={_renderWebActionSheet(false)}
          renderOwnerWebActionSheet={_renderWebActionSheet(true)}
        />
        <View style={{ marginTop: Spacing.l }}>
          <KitButton
            secondary
            title='Hide conversation'
            onPress={_askHideConversation}
          />
        </View>
        <View style={{ height: Spacing.xxl }} />
      </View>
    );
  };

  const _toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const _showConfirmAlert = (
    title: string,
    message: string,
    confirmButtonText: string,
    actionKey: string,
    confirmStyle:
      | 'cancel'
      | 'default'
      | 'destructive'
      | undefined = 'destructive'
  ) => {
    if (Platform.OS === 'web') {
      setConfirmActionTitle(title);
      setConfirmActionMessage(message);
      setConfirmActionButtonText(confirmButtonText);
      setConfirmActionKey(actionKey);
      setConfirmModalVisible(true);

      return;
    }

    Alert.alert(title, message, [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: confirmButtonText,
        onPress: () => _onConfirm(actionKey),
        style: confirmStyle,
      },
    ]);
  };

  const _renderWebActionSheet =
    (isOwner: boolean) => (member: ChannelMember, closeMenu: () => void) => {
      const _askUnblockUser = () => {
        setSelectedUser(member);
        const title = `Unblock ${member.nickname}`;
        const msg = `You will be able to receive new messages from ${
          member.nickname?.split(' ')?.[0]
        }, but not anything that may have been sent during the block.`;
        _showConfirmAlert(title, msg, 'Unblock', 'unblock-user');
        closeMenu();
      };

      const _askBlockUser = () => {
        setSelectedUser(member);
        const title = `Block ${member.nickname}`;
        const msg =
          'They will not be able to send or receive messages from you.';
        _showConfirmAlert(title, msg, 'Block', 'block-user');
        closeMenu();
      };

      const _askPromoteUser = () => {
        setSelectedUser(member);
        const title = `Change ${member.nickname}'s role to Manager`;
        const message =
          'They will have the ability to edit the group, delete messages, and remove Members.';
        _showConfirmAlert(title, message, 'Change', 'promote-user');
        closeMenu();
      };

      const _askDemoteUser = () => {
        setSelectedUser(member);
        const title = `Change ${member.nickname}'s role to Member`;
        const message =
          'They will lose the ability to edit the group, delete messages, and remove Members.';
        _showConfirmAlert(title, message, 'Change', 'demote-user');
        closeMenu();
      };

      const _askRemoveUser = () => {
        setSelectedUser(member);
        const title = `Remove ${member.nickname}`;
        const msg =
          'Are you sure you want to remove this member? They will no longer have access to the group or its messages.';
        _showConfirmAlert(title, msg, 'Remove', 'remove-user');
        closeMenu();
      };

      const enableBlockUserActions = channel?.customType === ChannelType.Direct;
      const isSelectedUserBlocked = member?.isBlockedByMe ?? false;
      const enableChannelActions = channel?.customType !== ChannelType.Direct;

      return (
        <>
          {enableBlockUserActions &&
            (isSelectedUserBlocked ? (
              <ActionSheetItem
                icon={'' as IconName}
                label='Unblock User'
                onPress={_askUnblockUser}
              />
            ) : (
              <ActionSheetItem
                icon='block'
                label='Block User'
                onPress={_askBlockUser}
              />
            ))}

          {enableChannelActions && (
            <ActionSheetItem
              icon='remove'
              label='Remove from group'
              onPress={_askRemoveUser}
            />
          )}

          {enableChannelActions &&
            (isOwner ? (
              <ActionSheetItem
                icon='person-circle'
                label='Change to Member'
                onPress={_askDemoteUser}
              />
            ) : (
              <ActionSheetItem
                icon='person-circle'
                label='Change role to Manager'
                onPress={_askPromoteUser}
              />
            ))}
        </>
      );
    };

  const _onConfirm = (action: string) => {
    setConfirmModalVisible(false);
    switch (action) {
      case 'leave-channel':
        _confirmLeaveChannel();
        break;

      case 'delete-channel':
        _confirmDeleteChannel();
        break;

      case 'hide-conversation':
        _confirmHideConversation();
        break;

      case 'unblock-user':
        // @ts-ignore (this will work with ChannelMember type as well)
        selectedUser && _confirmUnblockUser(channel, selectedUser);
        break;

      case 'block-user':
        // @ts-ignore (this will work with ChannelMember type as well)
        selectedUser && _confirmBlockUser(channel, selectedUser);
        break;

      case 'promote-user':
        selectedUser && _confirmPromoteUser(selectedUser);
        break;

      case 'demote-user':
        selectedUser && _confirmDemoteUser(selectedUser);
        break;

      case 'remove-user':
        selectedUser && _confirmRemoveUser(channel, selectedUser);
        break;
    }
  };

  const showKitSnack = (msg: string, dur: number) => {
    setSnackOptions({ visible: true, message: msg, duration: dur });
  };

  //****************************************************************************
  // Lifecycle
  //****************************************************************************

  useEffect(() => {
    if (channel) {
      dispatch(clearSelectedGroupChannel());
      updateMemberList();
      updateBlockedUserList();
      updateInvitedList();
      updateIsRestricted();
    }
  }, [channel]);

  const channelData = parseChannelData();

  return (
    <>
      <View style={styles.safeContainer}>
        <ScrollView>
          {channel?.customType !== ChannelType.Direct &&
            _renderChannelDetails()}
          {channel?.customType === ChannelType.Direct && _renderDirectDetails()}
        </ScrollView>

        <MemberActionSheet
          visible={modalVisible}
          setVisible={setModalVisible}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          isSelectedUserOwner={isSelectedUserOwner}
          isSelectedUserBlocked={selectedUser?.isBlockedByMe ?? false}
          enableChannelActions={channel?.customType !== ChannelType.Direct}
          enableBlockUserActions={channel?.customType === ChannelType.Direct}
          showKitSnack={showKitSnack}
        />
      </View>

      <Modal
        isVisible={confirmModalVisible}
        animationIn='fadeIn'
        animationOut='fadeOut'
      >
        <ConfirmModal
          title={confirmActionTitle}
          message={confirmActionMessage}
          cancelButtonText='Cancel'
          confirmButtonText={confirmActionButtonText}
          setConfirmModalVisible={setConfirmModalVisible}
          onConfirm={_onConfirm}
          action={confirmActionKey}
        />
      </Modal>

      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
    </>
  );
}

//******************************************************************************
// Styles
//******************************************************************************
const styles = StyleSheet.create({
  viewContainer: {
    flex: 1,
    paddingHorizontal: Spacing.l,
  },
  safeContainer: {
    flex: 1,
    backgroundColor: colorForScheme({ default: Colors.N0 }),
  },
  image: {
    borderRadius: 18,
    height: 36,
    width: 36,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colorForScheme({ default: Colors.brand }),
  },
  banner: {
    backgroundColor: Colors.N50,
    marginHorizontal: Spacing.l,
    borderRadius: 3,
    ...Platform.select({
      android: { elevation: 10, overflow: 'hidden' },
      default: {
        shadowColor: '#000000',
        shadowOpacity: 0.12,
        shadowOffset: { width: 0, height: 10 },
        shadowRadius: 25,
      },
    }),
    marginBottom: Spacing.l,
  },
});
